'use client'

import { useState, useCallback, useTransition } from 'react'
import Link from 'next/link'
import { Bookmark, Bell } from 'lucide-react'
import { JobBoardFilters, type JobFilters } from './job-board-filters'
import { JobBoardGrid } from './job-board-grid'
import { JobAlertModal } from './job-alert-modal'
import { getJobsForBoard } from '../_actions/job-search-actions'
import type { JobCardData } from '@/types/api.types'

interface JobBoardShellProps {
  initialJobs: JobCardData[]
  initialCursor: string | null
  isAuthenticated?: boolean
  /** Destination for the "Hiring? Post a job" CTA - only set for company accounts (null hides it). */
  postJobHref?: string | null
}

export function JobBoardShell({ initialJobs, initialCursor, isAuthenticated = true, postJobHref = null }: JobBoardShellProps) {
  const font = { fontFamily: 'var(--font-inter, Inter, sans-serif)' }
  const [jobs, setJobs] = useState<JobCardData[]>(initialJobs)
  const [cursor, setCursor] = useState<string | null>(initialCursor)
  const [filters, setFilters] = useState<JobFilters>({
    specialization: '',
    specializations: [],
    locationType: '',
    locationQuery: '',
    budgetType: '',
    budgetMin: null,
    budgetMax: null,
    jobType: '',
    search: '',
  })
  const [isPending, startTransition] = useTransition()
  const [alertsOpen, setAlertsOpen] = useState(false)

  const fetchJobs = useCallback(
    (newFilters: JobFilters, append = false, newCursor?: string) => {
      startTransition(async () => {
        const result = await getJobsForBoard({
          specialization: newFilters.specializations.length === 1 ? newFilters.specializations[0] : undefined,
          specializations: newFilters.specializations.length > 1 ? newFilters.specializations : undefined,
          locationType: newFilters.locationType || undefined,
          locationQuery: newFilters.locationQuery || undefined,
          budgetType: newFilters.budgetType || undefined,
          budgetMin: newFilters.budgetMin ?? undefined,
          budgetMax: newFilters.budgetMax ?? undefined,
          jobType: newFilters.jobType || undefined,
          search: newFilters.search || undefined,
          cursor: newCursor,
        })

        if (result.success && result.data) {
          setJobs((prev) => append ? [...prev, ...result.data!.jobs] : result.data!.jobs)
          setCursor(result.data.nextCursor)
        }
      })
    },
    [],
  )

  const handleFiltersChange = useCallback(
    (newFilters: JobFilters) => {
      setFilters(newFilters)
      fetchJobs(newFilters, false)
    },
    [fetchJobs],
  )

  const handleLoadMore = useCallback(() => {
    if (cursor && !isPending) {
      fetchJobs(filters, true, cursor)
    }
  }, [cursor, isPending, filters, fetchJobs])

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1
            className="text-2xl font-bold text-[var(--color-text-primary)]"
            style={{ fontFamily: 'var(--font-heading, "Plus Jakarta Sans", sans-serif)' }}
          >
            Job Board
          </h1>
          <p className="mt-1 text-sm text-[var(--color-text-secondary)]" style={font}>
            Find your next AI opportunity
          </p>
        </div>
        {/* Recruiter on-ramp - company accounts only. Individuals never see
            it (the board stays seeker-framed); the server decides the
            destination: straight to posting, or company onboarding if the
            company profile isn't finished. */}
        <div className="flex shrink-0 items-center gap-2">
          {/* Entry point for /jobs/saved — the page existed but had zero
              links pointing at it (2026-07-04 audit): bookmarked jobs were
              unreachable without typing the URL. */}
          {isAuthenticated && (
            <Link
              href="/jobs/saved"
              className="flex min-h-11 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-[var(--color-border-default)] px-4 py-2 text-sm font-medium text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-text-primary)] sm:min-h-0"
              style={font}
            >
              <Bookmark className="h-4 w-4" aria-hidden="true" />
              Saved
            </Link>
          )}
          {/* Job Alerts — the modal + backend (cron dispatcher, email) already
              existed but had no entry point (2026-08-02 audit): the feature was
              unreachable. This CTA opens it, seeded with the current filters. */}
          {isAuthenticated && (
            <button
              type="button"
              onClick={() => setAlertsOpen(true)}
              className="flex min-h-11 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-[var(--color-border-default)] px-4 py-2 text-sm font-medium text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-text-primary)] sm:min-h-0"
              style={font}
            >
              <Bell className="h-4 w-4" aria-hidden="true" />
              Get alerts
            </button>
          )}
          {postJobHref && (
            <Link
              href={postJobHref}
              className="shrink-0 whitespace-nowrap rounded-full bg-[var(--color-brand-indigo)] px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90"
              style={font}
            >
              Hiring? Post a job →
            </Link>
          )}
        </div>
      </div>

      {isAuthenticated && (
        <div className="mb-6">
          <JobBoardFilters
            filters={filters}
            onFiltersChange={handleFiltersChange}
            resultCount={jobs.length}
          />
        </div>
      )}

      <JobBoardGrid
        jobs={jobs}
        hasMore={isAuthenticated && cursor !== null}
        isLoading={isPending}
        onLoadMore={handleLoadMore}
        isAuthenticated={isAuthenticated}
      />

      {isAuthenticated && (
        <JobAlertModal
          isOpen={alertsOpen}
          onClose={() => setAlertsOpen(false)}
          currentFilters={filters as unknown as Record<string, unknown>}
        />
      )}
    </div>
  )
}
